/**
 * Who is signed in, and what they may do.
 *
 * `permissions` comes from the server, which is also the enforcement — `require()` in
 * `http/mod.rs`. This is politeness, not security: it decides what to render.
 */
import { useQuery } from "@tanstack/react-query";
import { me as fetchMe } from "../lib/api";
import type { Me } from "../lib/types";

export function useSession(): { me: Me | undefined; isPending: boolean } {
  const query = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    staleTime: Infinity,
    retry: false,
  });
  return { me: query.data, isPending: query.isPending };
}

/**
 * A predicate over the caller's permission strings.
 *
 * Controls the role cannot use are **not rendered**, not rendered-and-disabled: a
 * disabled button a user can never enable is an accusation.
 */
export function usePermission(): (permission: string) => boolean {
  const { me } = useSession();
  // Both links optional: an unexpected /v1/me body must not take the whole frame down
  // with it. A missing permission list means "may do nothing", which is the safe answer.
  return (permission: string) => me?.permissions?.includes(permission) ?? false;
}
